import React, { useState, useEffect } from 'react';
import { Header, Button, Grid, Dimmer, Loader, Form } from "semantic-ui-react";
import Swal from 'sweetalert2';
import UserInfo from '../Common/SharedComponents/UserInfo';
import AdminSidebar from '../Common/SharedComponents/AdminSidebar';
import axiosInstance, { errorMessageForCode } from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { ChangePasswordModel } from '../../types/UserDTO';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

type FormErrorFields = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ChangePasswordForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<ChangePasswordModel>({} as ChangePasswordModel);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    document.title = 'Change Password - Adjure';
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            oldPassword: !formValues?.oldPassword ? 'Old password is required' : (!utils.isValidPassword(formValues.oldPassword) ? 'Please provide a least 8 characters, one numeric character and one special character' : ''),
            newPassword: !formValues?.newPassword ? 'New password is required' : (!utils.isValidPassword(formValues.newPassword) ? 'Please provide a least 8 characters, one numeric character and one special character' : ''),
            confirmPassword: !formValues?.confirmPassword ? 'Confirm password is required' : (formValues?.newPassword !== formValues?.confirmPassword) ? 'Password & confirm password should be same' : '',
          };
        } else {
          switch (fieldName) {
            case 'oldPassword':
              errors.oldPassword = !value ? 'Old password is required' : (!utils.isValidPassword(value) ? 'Please provide a least 8 characters, one numeric character and one special character' : '');
              break;
            case 'newPassword':
              errors.newPassword = !value ? 'New password is required' : (!utils.isValidPassword(value) ? 'Please provide a least 8 characters, one numeric character and one special character' : '');
              break;
            case 'confirmPassword':
              errors.confirmPassword = !value ? 'Confirm password is required' : (value !== formValues?.newPassword) ? 'New password & confirm password should be same' : '';
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = {
        oldPassword: formValues.oldPassword,
        newPassword: formValues.newPassword
      };
      Swal.fire({
        title: 'Are you sure you want to submit?',
        confirmButtonText: 'Submit',
        showConfirmButton: true,
        showDenyButton: true,
        denyButtonText: 'Cancel',
        denyButtonColor: '#657193'
      }).then((result) => {
        if (result.isConfirmed) {
          setIsLoading(true);
          axiosInstance.post(`/Auth/ResetPassword`, payload).then(({ data, status, statusText }) => {
            setIsLoading(false);
            if (status === 200) {
              Swal.fire({
                title: 'Password changed successfully',
                icon: 'success',
                confirmButtonText: 'Ok',
                showConfirmButton: true,
                showDenyButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false
              }).then((result) => {
                if (result.isConfirmed) {
                  window.location.reload();
                }
              });
            }
          })
            .catch((error) => {
              setIsLoading(false);
              Swal.fire({
                icon: 'error',
                title: 'Ope!',
                html: `<p>${error?.response?.data?.Message ? error.response.data.Message : error.message}</p><p>(${errorMessageForCode(error?.response?.status)})</p>`,
                confirmButtonText: 'OK',
              });
            });
        }
      });
    }
  };

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">Change Password</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <SwitchTheme />
        </div>
      </div>
      <div className="section__body">
        <Dimmer.Dimmable as="div" dimmed={isLoading}>
          <Dimmer active={isLoading} inverted={true}>
            <Loader>Loading</Loader>
          </Dimmer>
          <Grid padded>
            <Grid.Row>
              <Grid.Column mobile={16} tablet={3} computer={3}>
                <AdminSidebar />
              </Grid.Column>
              <Grid.Column mobile={16} tablet={6} computer={6}>
                <Form className='form-custom'>
                  <h2 style={{ marginTop: '1rem' }}>Change Password</h2>
                  <p style={{ textAlign: 'left' }}>Your password must be minimum least 8 characters long and must include one numeric character and one special character</p>
                  <Form.Field>
                    <Form.Input
                      type="password"
                      label="Old Password"
                      name="oldPassword"
                      value={formValues.oldPassword}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={!!formErrors?.oldPassword}
                      required
                    />
                    {formErrors?.oldPassword && <div className="error-message" style={{ color: 'red', textAlign: 'left' }}>{formErrors?.oldPassword}</div>}
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      type="password"
                      label="New Password"
                      name="newPassword"
                      value={formValues.newPassword}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={!!formErrors?.newPassword}
                      required
                    />
                    {formErrors?.newPassword && <div className="error-message" style={{ color: 'red', textAlign: 'left' }}>{formErrors?.newPassword}</div>}
                  </Form.Field>
                  <Form.Field>
                    <Form.Input
                      type="password"
                      label="Confirm Password"
                      name="confirmPassword"
                      value={formValues.confirmPassword}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={!!formErrors?.confirmPassword}
                      required
                    />
                    {formErrors?.confirmPassword && <div className="error-message" style={{ color: 'red', textAlign: 'left' }}>{formErrors?.confirmPassword}</div>}
                  </Form.Field>
                  <Grid style={{ marginBottom: '1rem' }}>
                    <Grid.Column>
                      <Button type="submit" onClick={handleSubmit} className='violet button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', fontSize: '1.6rem' }}>
                        Submit
                      </Button>
                    </Grid.Column>
                  </Grid>
                </Form>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Dimmer.Dimmable>
      </div>
    </section>
  );
}

export default ChangePasswordForm;